import type { BackendProductUpsertOperation, Validations, Violation } from "@credit-acceptance/step6-bff-client";
import { BackendProductUpsertOperationNameEnum } from "@credit-acceptance/step6-bff-client";
import type { FieldPath } from "react-hook-form";

import { stripFieldPrefix, type TranslatedViolations, translateViolations } from "../../serverViolations";
import type { ObpFormValues } from "./Obp.mapping";

// ---------------------------------------------------------------------------
// Obp.violations
//
// Maps `additionalProducts.obp.validations` onto the rendered product forms.
// Confirmed grammar: a bare BackendProductUpsertOperation field name plus
// `context.productName`. Also accepted, unconfirmed: `obp.products.<i>.<field>`
// and `products.<i>.<field>`, where `context.productName` (when present) still
// wins over the index. Anything that doesn't land on a visible input is
// returned in `unmapped` for the section banner.
// ---------------------------------------------------------------------------

type ObpProductField = keyof ObpFormValues["products"][number];

const FIELD_MAP: Record<string, ObpProductField> = {
  vendorName: "vendorName",
  certificateNumber: "certificateNumber",
  sellingPrice: "sellingPrice",
  termMonths: "termMonths",
  termMiles: "termMiles",
  unlimitedMileage: "unlimitedMileage",
  effectiveDate: "effectiveDate",
  formId: "formNumber",
  formNumber: "formNumber",
  selectedProducts: "selectedProducts",
};

const APPEARANCE_PROTECTION_FIELDS = new Set<ObpProductField>([
  "vendorName",
  "sellingPrice",
  "effectiveDate",
  "formNumber",
]);

/**
 * Mirrors StandardProductFields' per-product layout.
 * @param {ObpProductField} field - The form field.
 * @param {BackendProductUpsertOperationNameEnum} productName - The rendered product.
 * @returns {boolean} Whether that product renders the field.
 */
function isFieldVisible(field: ObpProductField, productName: BackendProductUpsertOperationNameEnum): boolean {
  const isBundle = productName === BackendProductUpsertOperationNameEnum.BundledProducts;
  if (productName === BackendProductUpsertOperationNameEnum.AppearanceProtection) {
    return APPEARANCE_PROTECTION_FIELDS.has(field);
  }
  return (field !== "selectedProducts" || isBundle) && (field !== "unlimitedMileage" || !isBundle);
}

/**
 * @param {Violation} violation - A violation.
 * @returns {string | undefined} The BFF's `context.productName` discriminator, when it is a string.
 */
function productNameOf(violation: Violation): string | undefined {
  const productName: unknown = violation.context?.productName;
  return typeof productName === "string" ? productName : undefined;
}

/**
 * Resolves one violation to a visible product field.
 * @param {Violation} violation - The violation.
 * @param {Array<Pick<BackendProductUpsertOperation, "name">>} products - The rendered products, in form order.
 * @returns {FieldPath<ObpFormValues> | undefined} The RHF path, or undefined when no visible input matches.
 */
function resolveFieldPath(
  violation: Violation,
  products: Array<Pick<BackendProductUpsertOperation, "name">>
): FieldPath<ObpFormValues> | undefined {
  if (!violation.field) {
    return undefined;
  }
  const segments = stripFieldPrefix(violation.field, ["obp"]).split(".");
  const indexed = segments.length === 3 && segments[0] === "products";
  if (!indexed && segments.length !== 1) {
    return undefined;
  }
  const field = FIELD_MAP[segments.at(-1) ?? ""];
  const productName = productNameOf(violation);
  const indexFromPath = indexed ? Number(segments[1]) : -1;
  const index =
    productName === undefined ? indexFromPath : products.findIndex((product) => product.name === productName);
  const product = Number.isInteger(index) ? products[index] : undefined;
  if (!field || !product || !isFieldVisible(field, product.name)) {
    return undefined;
  }
  return `products.${index}.${field}`;
}

/**
 * Translates both OBP violation buckets onto the rendered products.
 * @param {Validations | undefined} validations - The GET or PUT `obp.validations`.
 * @param {Array<Pick<BackendProductUpsertOperation, "name">>} products - The rendered products, in form order.
 * @returns {TranslatedViolations<FieldPath<ObpFormValues>>} Field errors plus unmapped findings.
 */
export function translateObpViolations(
  validations: Validations | undefined,
  products: Array<Pick<BackendProductUpsertOperation, "name">>
): TranslatedViolations<FieldPath<ObpFormValues>> {
  return translateViolations(validations, (violation) => resolveFieldPath(violation, products));
}

/**
 * Banner copy for an unmapped OBP violation, prefixed with its product when the BFF named one.
 * @param {Violation} violation - The unmapped violation.
 * @returns {string} The message.
 */
export function formatObpUnmapped(violation: Violation): string {
  const productName = productNameOf(violation);
  return productName ? `${productName}: ${violation.message}` : violation.message;
}
